import { useEffect } from 'react';

import { useQuery, useSuspenseQueries } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

import { faFileDownload } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Box, Button, LinearProgress, Typography } from '@mui/material';

import { claimQueries } from '@repo/api/claim/claims';
import { questionnaireQueries } from '@repo/api/claim/questionnaire';
import {
	downloadExtractSalesAnalysis,
	downloadExtractSalesFiles,
} from '@repo/api/shared/generics';
import { EIntervalType } from '@repo/api/types/claim/claims';
import { useUser } from '@repo/auth';
import {
	DailySalesAnalysisTable,
	MonthlySalesAnalysisTable,
	WeeklySalesAnalysisTable,
} from '@repo/features/calculations';
import useCurrency from '@repo/features/hooks/useCurrency';
import { downloadFile } from '@repo/ui/utils/download';

import { SalesAnalysisFootnotes } from './-components/SalesAnalysisFootnotes';
import { Route as BILossRoute } from './loss';

export const Route = createFileRoute(
	'/_claims/claim/$claimId/_claim/calculations/salesAnalysis',
)({
	component: function SalesAnalysisCalculationsPage() {
		const { claimId } = Route.useParams();
		const navigate = Route.useNavigate();
		const { permissions } = useUser();
		const [{ data: claim }, { data: questionnaire }] = useSuspenseQueries({
			queries: [claimQueries.id(claimId), questionnaireQueries.id(claimId)],
		});

		const { data, isFetching, error } = useQuery(
			claimQueries.salesAnalysis(claimId),
		);
		const currency = useCurrency(claim.claimInsured.countryId);
		const canDownloadSalesDocuments = permissions.includes(
			'claim.sales.download',
		);
		const canExtractSalesAnalysis = permissions.includes('claim.sales.extract');

		useEffect(() => {
			// Keep direct calculation URLs consistent with the disabled tab state.
			if (!claim.isTier2Enabled || !questionnaire.isTier2) {
				void navigate({
					to: BILossRoute.to,
				});
			}
		}, [claim.isTier2Enabled, questionnaire.isTier2, navigate]);

		if (isFetching) {
			return <LinearProgress />;
		}

		if (error) {
			return (
				<Typography variant="body2" color="text.secondary">
					Error loading calculations
				</Typography>
			);
		}

		if (!data) {
			return (
				<Typography variant="body2" color="text.secondary">
					Calculations not found
				</Typography>
			);
		}

		const { salesPeriodData } = data;

		return (
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					gap: 3,
					width: '100%',
					maxWidth: '1000px',
					mx: 'auto',
					py: 2,
				}}
			>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						gap: 2,
						flexWrap: 'wrap',
					}}
				>
					<Typography variant="h6" component="h2" sx={{ fontWeight: 'bold' }}>
						Sales Analysis
					</Typography>
					<Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
						{canDownloadSalesDocuments && (
							<Button
								variant="outlined"
								color="button"
								startIcon={<FontAwesomeIcon icon={faFileDownload} />}
								onClick={() =>
									downloadExtractSalesFiles(claimId).then(downloadFile)
								}
							>
								Download all Sales Documents
							</Button>
						)}
						{canExtractSalesAnalysis && (
							<Button
								variant="outlined"
								color="button"
								startIcon={<FontAwesomeIcon icon={faFileDownload} />}
								onClick={() =>
									downloadExtractSalesAnalysis(claimId).then(downloadFile)
								}
							>
								Extract Sales Analysis
							</Button>
						)}
					</Box>
				</Box>

				{salesPeriodData.intervalType === EIntervalType.Daily && (
					<DailySalesAnalysisTable
						rows={salesPeriodData.rows}
						currencySymbol={currency.currencySymbol}
					/>
				)}
				{salesPeriodData.intervalType === EIntervalType.Weekly && (
					<WeeklySalesAnalysisTable
						rows={salesPeriodData.rows}
						currencySymbol={currency.currencySymbol}
						weeklyAverage={salesPeriodData.weeklyAverage}
					/>
				)}
				{salesPeriodData.intervalType === EIntervalType.Monthly && (
					<MonthlySalesAnalysisTable
						rows={salesPeriodData.rows}
						currencySymbol={currency.currencySymbol}
						periodsPriorToLoss={salesPeriodData.periodsPriorToLoss}
					/>
				)}
				<SalesAnalysisFootnotes data={salesPeriodData} />
			</Box>
		);
	},
});
